import type { HTMLAttributes, ReactNode } from "react";

import { useI18n } from "../use-i18n";
import { cn, cv, type VariantProps } from "../utils";
import { NcIconExclamationCircle, NcIconInformationCircle, NcIconTickCircle } from "./nc-icons";

const alert = cv(
  [
    "relative grid grid-cols-[auto_1fr_auto] items-start gap-2 rounded-md border px-3 py-3 pl-5 pr-3 min-w-0",
    "before:content-[''] before:absolute before:top-0 before:bottom-0 before:left-[-1px] before:w-1.5 before:rounded-l-md",
  ],
  {
    variants: {
      variant: {
        default: "border-main/45 bg-main/5 before:bg-main",
        success: "border-success/45 bg-success/5 before:bg-success",
        danger: "border-danger/45 bg-danger/5 before:bg-danger",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const heading = "font-bold text-lg";
const iconBase = "pt-1 leading-none";
const wrapper = "flex flex-col gap-1 h-full";
const content = "flex flex-col gap-2 items-start grow";

const colourVariant = {
  default: "text-main",
  success: "text-success",
  danger: "text-danger",
};

export interface NcAlertProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "title">, VariantProps<typeof alert> {
  title?: string | ReactNode;
  icon?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}

const getAlertIcon = (t: (value: string) => string, variant?: string) => {
  switch (variant) {
    case "success":
      return <NcIconTickCircle alt="" />;
    case "danger":
      return <NcIconExclamationCircle alt={t("alert")} />;
    default:
      return <NcIconInformationCircle alt="" />;
  }
};

export const NcAlert = ({
  variant = "default",
  icon,
  title,
  action,
  children,
  className,
  ...props
}: NcAlertProps) => {
  const { t } = useI18n();
  return (
    <div className={cn(alert({ variant }), className)} {...props} data-nc="NcAlert">
      <div className={cn(iconBase, colourVariant[variant ?? "default"])}>
        {icon ?? getAlertIcon(t, variant as string)}
      </div>
      <div className={wrapper}>
        {title && <div className={heading}>{title}</div>}
        {children && <div className={content}>{children}</div>}
      </div>
      {action && <>{action}</>}
    </div>
  );
};
